import useAxios from "src/core/shared/hooks/useAxios";
import {
  PaginatedParams,
  FilterParams,
  PaginatedData
} from "src/core/shared/api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { StringResponse } from "src/modules/support/api/support.api";
import {
  CustomerBranchFilterDefault,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";

export type ListLeadTimeOverridesParams = { pagination: PaginatedParams<ILeadTimeOverride>; filters: FilterParams<ILeadTimeOverride> };
export type UpdateLeadTimeOverrideParams = {leadTimeOverrideId: number, leadTimeOverrideUpdate: Partial<ILeadTimeOverride>};

type LeadTimeOverrideEndpointConfig = {
  LIST_LEAD_TIME_OVERRIDES: EndpointConfig<
    ListLeadTimeOverridesParams,
    PaginatedData<ILeadTimeOverride>
  >;
  GET_LEAD_TIME_OVERRIDE: EndpointConfig<
    number,
    ILeadTimeOverride
  >;
  DELETE_LEAD_TIME_OVERRIDE: EndpointConfig<
    number,
    StringResponse
  >;
  CREATE_LEAD_TIME_OVERRIDE: EndpointConfig<
    Omit<ILeadTimeOverride, "id">,
    ILeadTimeOverride
  >;
  UPDATE_LEAD_TIME_OVERRIDE: EndpointConfig<
    UpdateLeadTimeOverrideParams,
    ILeadTimeOverride
  >;
};

const requests: RequestConfig<LeadTimeOverrideEndpointConfig> = {
  LIST_LEAD_TIME_OVERRIDES: params => ({
    url: `${process.env.REACT_APP_API_URL}/lead-time-override/all`,
    method: "GET",
    params
  }),
  GET_LEAD_TIME_OVERRIDE: leadTimeOverrideId => ({
    url: `${process.env.REACT_APP_API_URL}/lead-time-override/${leadTimeOverrideId}`,
    method: "GET"
  }),
  DELETE_LEAD_TIME_OVERRIDE: leadTimeOverrideId => ({
    url: `${process.env.REACT_APP_API_URL}/lead-time-override/${leadTimeOverrideId}`,
    method: "DELETE"
  }),
  CREATE_LEAD_TIME_OVERRIDE: leadTimeOverride => ({
    url: `${process.env.REACT_APP_API_URL}/lead-time-override/create`,
    data: leadTimeOverride,
    method: "POST"
  }),
  UPDATE_LEAD_TIME_OVERRIDE: ({ leadTimeOverrideId, leadTimeOverrideUpdate }) => ({
    url: `${process.env.REACT_APP_API_URL}/lead-time-override/${leadTimeOverrideId}`,
    data: leadTimeOverrideUpdate,
    method: "PATCH"
  })
};

export const useLeadTimeOverrideApi: UseApiRequest<LeadTimeOverrideEndpointConfig> = name => useAxios(requests[ name ], name);

export interface ILeadTimeOverride {
  id: number;
  /** Descriptive name for the override */
  name?: string;
  /** Target order delivery date to override. Format: YYYY-MM-DD */
  deliveryDate: string;
  /** The cutopff time to use that overrides any other cutoff time set. Format YYYY-MM-DD HH:MM:SS */
  cutoffDateTime: string;
  /** Whether by default the override should be allow all or allow only the added filters be default */
  type: CustomerBranchFilterDefault;
  /** Optional filters to either include or exclude customers/drops */
  filters: ILeadTimeOverrideFilter[];
  }

export interface ILeadTimeOverrideFilter {
    id: number;
    /** Association id to the lead time override */
    leadTimeOverrideId: number;
    /** Customer to associate with */
    customerId?: number | null;
    /** Branch to associate with */
    branchId?: number | null;
    /** Whether to include this drop to allow them to deliver or exclude them */
    filterType: CustomerBranchFilterType;
  }

export type CreateLeadTimeOverrideFilterParams = Omit<ILeadTimeOverrideFilter, "id" | "leadTimeOverrideId"> & Partial<Pick<ILeadTimeOverrideFilter, "id" | "leadTimeOverrideId">>;
